/**
 * Canonical source of truth for format categories and their normalized values.
 * Values are produced by the normalize_format() ETL macro at import time.
 */

import { z } from "zod";

/**
 * Maps format categories to normalized format keys used in denormalized release/master format arrays.
 */
export const FORMAT_GROUPS = {
  vinyl: [
    "12_vinyl",
    "10_vinyl",
    "7_vinyl",
    "flexi_disc",
    "shellac",
    "picture_disc",
    "lathe_cut",
    "acetate",
    "pathe_disc",
    "edison_disc",
    "cylinder",
  ],
  cd: ["cd", "cd_r", "sacd", "hybrid_sacd", "dts_cd", "cd_g", "dualdisc", "minidisc", "cdv"],
  cassette: [
    "cassette",
    "microcassette",
    "8_track_cartridge",
    "4_track_cartridge",
    "dcc",
    "dat",
    "reel_to_reel",
    "playtape",
    "elcaset",
    "nt_cassette",
    "rca_tape",
  ],
  video: [
    "dvd",
    "dvd_r",
    "blu_ray",
    "blu_ray_r",
    "uhd_blu_ray",
    "vhs",
    "betamax",
    "laserdisc",
    "hd_dvd",
    "umd",
    "vhd",
    "selectavision",
    "betacam",
    "umatic",
    "minidv",
    "video8",
    "vcd",
  ],
} as const;

/**
 * Format categories derived from FORMAT_GROUPS keys.
 * Use for search/browse queries where "other" is not a valid option.
 */
export const FORMAT_CATEGORIES = ["vinyl", "cd", "cassette", "video"] as const;

/**
 * Extended format categories including "other" catch-all.
 * Use for artist discography filters and format counting.
 */
export const FORMAT_CATEGORIES_WITH_OTHER = ["vinyl", "cd", "cassette", "video", "other"] as const;

export const formatCategoryEnum = z.enum(FORMAT_CATEGORIES);
export const formatCategoryWithOtherEnum = z.enum(FORMAT_CATEGORIES_WITH_OTHER);

export type FormatCategory = z.infer<typeof formatCategoryEnum>;
export type FormatCategoryWithOther = z.infer<typeof formatCategoryWithOtherEnum>;
